import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/lib/routes';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Si fourni, redirige vers '/' si la permission manque (OR logic sur tableau) */
  requirePermission?: string | string[];
  /** Si true, AND logic sur requirePermission */
  requireAll?: boolean;
}

/**
 * ProtectedRoute — garde d'accès global.
 *
 * 1. Non authentifié → /login (state.from conservé pour redirect post-login)
 * 2. Authentifié mais permission manquante → / (accueil safe)
 * 3. OK → rendu enfants
 */
export function ProtectedRoute({
  children,
  requirePermission,
  requireAll = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, hasPermission, hasAllPermissions } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }

  if (requirePermission) {
    const perms = Array.isArray(requirePermission) ? requirePermission : [requirePermission];
    const allowed = requireAll ? hasAllPermissions(perms) : hasPermission(perms);
    if (!allowed) {
      return <Navigate to={ROUTES.root} replace />;
    }
  }

  return <>{children}</>;
}
